/**
 * AgencyImageLibrary — per-agency image library modal (logos to start; the schema
 * and this UI generalise to hero/photo later).
 *
 * Shows every image for one agency (thumbnail, source, license badge, state), and
 * lets a user: manually "Fetch logos" (the manual trigger — no cron), upload their
 * own image (click or drag-drop), set one as the default, or reject/delete a bad
 * or ill-licensed pick. Provenance (source + license + attribution) is shown on
 * every card so a human can judge before promoting it.
 *
 * Plain CSS + CSS variables + Lucide icons — no MUI (per repo constraint). Dark/
 * light theming comes from the shared [data-theme] tokens.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, RefreshCw, Upload, Star, Check, Trash2, Ban, Loader2, ImageOff, ExternalLink, Crop,
} from 'lucide-react';
import useModalBehavior from '../../hooks/useModalBehavior';
import agencyImagesApi from '../../lib/agencyImagesApi';
import './AgencyImageLibrary.css';

// AI class → badge tone. Real marks read positive; photo/icon/unrelated warn.
function aiTone(klass = '') {
  if (['logo', 'seal', 'wordmark'].includes(klass)) return 'good';
  if (klass === 'icon') return 'mid';
  if (['photo', 'unrelated'].includes(klass)) return 'low';
  return 'mid';
}

// Map a license string to a badge tone.
function licenseTone(license = '') {
  const l = license.toLowerCase();
  if (l.includes('public domain') || l.includes('cc0') || l.startsWith('cc ') || l.includes('creative commons')) return 'ok';
  if (l.includes('user-provided')) return 'info';
  if (!l || l === 'unknown') return 'muted';
  return 'warn';
}

function ImageThumb({ image }) {
  const [broken, setBroken] = useState(false);
  const src = image.thumb_url || image.public_url; // normalized preview (WYSIWYG)
  if (broken || !src) {
    return <div className="ail-thumb ail-thumb-broken"><ImageOff size={22} /></div>;
  }
  return (
    <div className="ail-thumb">
      <img src={src} alt={image.source_name || 'agency image'} loading="lazy" onError={() => setBroken(true)} />
    </div>
  );
}

// Drag-a-box crop over the ORIGINAL image. Emits {x,y,w,h} fractions (0-1) that
// the backend applies to the source before re-normalizing the thumbnail.
function CropModal({ agencyId, image, onClose, onSaved }) {
  const imgRef = useRef(null);
  const [rect, setRect] = useState(image.crop_rect || null);
  const [drag, setDrag] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const toFrac = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX);
    const cy = (e.touches ? e.touches[0].clientY : e.clientY);
    return {
      x: Math.max(0, Math.min(1, (cx - r.left) / r.width)),
      y: Math.max(0, Math.min(1, (cy - r.top) / r.height)),
    };
  };
  const onDown = (e) => { e.preventDefault(); const p = toFrac(e); setDrag(p); setRect({ x: p.x, y: p.y, w: 0, h: 0 }); };
  const onMove = (e) => {
    if (!drag) return;
    const p = toFrac(e);
    setRect({ x: Math.min(drag.x, p.x), y: Math.min(drag.y, p.y), w: Math.abs(p.x - drag.x), h: Math.abs(p.y - drag.y) });
  };
  const onUp = () => setDrag(null);

  const save = async (r) => {
    setBusy(true); setErr(null);
    try { await agencyImagesApi.crop(agencyId, image.id, r); onSaved?.(); onClose(); }
    catch (e) { setErr(e.message || 'Crop failed'); } finally { setBusy(false); }
  };

  const valid = rect && rect.w > 0.02 && rect.h > 0.02;
  return (
    <div className="ail-overlay" onMouseUp={onUp} onMouseMove={onMove} onTouchEnd={onUp} onTouchMove={onMove}>
      <div className="ail-modal ail-crop-modal" role="dialog" aria-label="Crop logo" onClick={(e) => e.stopPropagation()}>
        <div className="ail-header">
          <div>
            <h2 className="ail-title">Crop logo</h2>
            <p className="ail-subtitle">Drag a box around the part to keep (e.g. just the emblem)</p>
          </div>
          <button className="ail-icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        {err && <div className="ail-error">{err}</div>}
        <div className="ail-crop-stage">
          <div className="ail-crop-imgwrap" onMouseDown={onDown} onTouchStart={onDown}>
            <img ref={imgRef} src={image.public_url} alt="" draggable={false} className="ail-crop-img" />
            {rect && (
              <div className="ail-crop-box" style={{ left: `${rect.x * 100}%`, top: `${rect.y * 100}%`, width: `${rect.w * 100}%`, height: `${rect.h * 100}%` }} />
            )}
          </div>
        </div>
        <div className="ail-toolbar">
          <button className="ail-btn ail-btn-primary" disabled={busy || !valid} onClick={() => save(rect)}>
            {busy ? <Loader2 size={15} className="ail-spin" /> : <Check size={15} />} Save crop
          </button>
          <button className="ail-btn" disabled={busy} onClick={() => save(null)}>Reset to full</button>
          <button className="ail-btn" disabled={busy} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function AgencyImageLibrary({ agencyId, agencyName, onClose, onDefaultChanged }) {
  const modal = useModalBehavior(onClose);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [cropping, setCropping] = useState(null); // image being cropped
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await agencyImagesApi.list(agencyId);
      setImages(r.images || []);
    } catch (e) {
      setError(e.message || 'Failed to load images');
    } finally {
      setLoading(false);
    }
  }, [agencyId]);

  useEffect(() => { load(); }, [load]);

  const doFetch = async () => {
    setFetching(true); setError(null);
    try {
      const r = await agencyImagesApi.fetchLogos(agencyId);
      setImages(r.images || []);
      if (r.autoPromoted) onDefaultChanged?.();
      if ((r.stored ?? 0) === 0 && (r.defaultImageId == null)) {
        setError('No logos found automatically — try uploading one.');
      }
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setFetching(false);
    }
  };

  const doUpload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { setError('Please choose an image file'); return; }
    setUploading(true); setError(null);
    try {
      await agencyImagesApi.upload(agencyId, file);
      await load();
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const act = async (id, fn, notifyDefault) => {
    setBusyId(id); setError(null);
    try {
      await fn();
      await load();
      if (notifyDefault) onDefaultChanged?.();
    } catch (e) {
      setError(e.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <div className="ail-overlay" {...modal.overlayProps}>
      <div className="ail-modal" {...modal.dialogProps} role="dialog" aria-label={`Image library for ${agencyName || 'agency'}`}>
        <div className="ail-header">
          <div>
            <h2 className="ail-title">Image Library</h2>
            <p className="ail-subtitle">{agencyName || `Agency #${agencyId}`}</p>
          </div>
          <button className="ail-icon-btn" onClick={modal.requestClose} aria-label="Close"><X size={18} /></button>
        </div>

        <div className="ail-toolbar">
          <button className="ail-btn ail-btn-primary" onClick={doFetch} disabled={fetching}>
            {fetching ? <Loader2 size={15} className="ail-spin" /> : <RefreshCw size={15} />}
            {fetching ? 'Fetching…' : 'Fetch logos'}
          </button>
          <button className="ail-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 size={15} className="ail-spin" /> : <Upload size={15} />}
            {uploading ? 'Uploading…' : 'Upload image'}
          </button>
          <input
            ref={fileRef} type="file" accept="image/*" hidden
            onChange={(e) => doUpload(e.target.files?.[0])}
          />
        </div>

        {error && <div className="ail-error">{error}</div>}

        <div
          className={`ail-dropzone ${dragOver ? 'ail-dropzone-active' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          {loading ? (
            <div className="ail-empty"><Loader2 size={20} className="ail-spin" /> Loading…</div>
          ) : images.length === 0 ? (
            <div className="ail-empty">
              No images yet. Click <strong>Fetch logos</strong> to source candidates, or drop an image here to upload.
            </div>
          ) : (
            <div className="ail-grid">
              {images.map((img) => {
                const busy = busyId === img.id;
                const rejected = img.status === 'rejected';
                return (
                  <div key={img.id} className={`ail-card ${img.is_default ? 'ail-card-default' : ''} ${rejected ? 'ail-card-rejected' : ''}`}>
                    <ImageThumb image={img} />

                    <div className="ail-badges">
                      {img.is_default && <span className="ail-badge ail-badge-default"><Star size={11} /> Default</span>}
                      <span className={`ail-badge ail-badge-${img.status}`}>{img.status}</span>
                      {img.quality_score != null && (
                        <span className={`ail-badge ail-badge-q ${img.quality_score >= 60 ? 'q-good' : img.quality_score >= 35 ? 'q-mid' : 'q-low'}`}
                          title="Heuristic quality score (0–100)">Q {Math.round(img.quality_score)}</span>
                      )}
                      {img.ai_class && (
                        <span className={`ail-badge ail-badge-ai ai-${aiTone(img.ai_class)}`}
                          title={img.ai_notes ? `AI: ${img.ai_notes}${img.ai_model ? ` (${img.ai_model})` : ''}` : 'AI vision rating'}>
                          🤖 {img.ai_class}{img.ai_score != null ? ` ${Math.round(img.ai_score)}` : ''}
                        </span>
                      )}
                      {img.is_photo && !img.ai_class && <span className="ail-badge ail-badge-photo" title="Looks like a photo, not a logo">📷 photo</span>}
                      {img.width && img.height && <span className="ail-badge ail-badge-dim">{img.width}×{img.height}</span>}
                    </div>

                    <div className="ail-meta">
                      <div className="ail-source" title={img.source_name}>{img.source_name || 'Unknown source'}</div>
                      <div className="ail-prov">
                        <span className={`ail-lic ail-lic-${licenseTone(img.license)}`}>{img.license || 'unknown'}</span>
                        {img.attribution && img.attribution !== 'unknown' && (
                          <span className="ail-attr" title={img.attribution}>· {img.attribution}</span>
                        )}
                      </div>
                      {img.source_url && img.source_url !== 'user-upload' && (
                        <a className="ail-src-link" href={img.source_url} target="_blank" rel="noreferrer">
                          source <ExternalLink size={11} />
                        </a>
                      )}
                    </div>

                    <div className="ail-actions">
                      {!img.is_default && !rejected && (
                        <button className="ail-mini ail-mini-primary" disabled={busy} onClick={() => act(img.id, () => agencyImagesApi.setDefault(agencyId, img.id), true)}>
                          {busy ? <Loader2 size={13} className="ail-spin" /> : <Check size={13} />} Set default
                        </button>
                      )}
                      {!rejected && (
                        <button className="ail-mini" disabled={busy} onClick={() => setCropping(img)} title="Crop the thumbnail">
                          <Crop size={13} /> Crop
                        </button>
                      )}
                      {!rejected && (
                        <button className="ail-mini" disabled={busy} onClick={() => act(img.id, () => agencyImagesApi.setStatus(agencyId, img.id, 'rejected'))} title="Mark as rejected">
                          <Ban size={13} /> Reject
                        </button>
                      )}
                      <button className="ail-mini ail-mini-danger" disabled={busy} onClick={() => act(img.id, () => agencyImagesApi.remove(agencyId, img.id), img.is_default)} title="Delete permanently">
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="ail-footer">
          <span className="ail-hint">Review each source &amp; license before setting a default. Auto-picked logos may be wrong — this is your override.</span>
        </div>
      </div>

      {cropping && (
        <CropModal
          agencyId={agencyId}
          image={cropping}
          onClose={() => setCropping(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
